import React, { useEffect, useRef, useState, useCallback } from "react";
import { replaceableValue } from "./AddContract";
import {
  getEditorElement,
  storeSelection,
  restoreSelection,
} from "./customEditorUtils";
import "./CustomEditorToolbar.scss";
import Bold from "assets/images/Text Format/Bold.svg";
import Bullet from "assets/images/Text Format/Bulleted List.svg";
import Clear from "assets/images/Text Format/Clear Format.svg";
import Italic from "assets/images/Text Format/Italic.svg";
import NumberList from "assets/images/Text Format/Numbered List.svg";
import Picker from "assets/images/Text Format/Picker.svg";
import Underline from "assets/images/Text Format/Underline.svg";
import Indent from "assets/images/Text Format/Indent Increase.svg";
import Outdent from "assets/images/Text Format/Indent Decrease.svg";
import BoldActive from "assets/images/Text Format/Bold-Hover.svg";
import BulletActive from "assets/images/Text Format/Bulleted List-Hover.svg";
import ItalicActive from "assets/images/Text Format/Italic-Hover.svg";
import NumberListActive from "assets/images/Text Format/Numbered List-Hover.svg";
import PickerActive from "assets/images/Text Format/Picker-Hover.svg";
import UnderlineActive from "assets/images/Text Format/Underline-Hover.svg";
import IndentActive from "assets/images/Text Format/Indent Increase-Hover.svg";
import OutdentActive from "assets/images/Text Format/Indent Decrease-Hover.svg";
import moment from "moment";
export const clearAllFormatting = () => {
  document.execCommand("removeFormat", false);
  document.execCommand("styleWithCSS", false, "false");
};

const CustomEditorToolbar = ({
  editorContainerRef,
  editorContentRef,
  editorKey,
  placeholderDropdownOpen,
  setPlaceholderDropdownOpen,
  contractData,
}) => {
  const lastSelectionRef = useRef(null);
  const colorInputRef = useRef(null);
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    orderedList: false,
    unorderedList: false,
    color: false,
    colorValue: null,
    indent: false,
    outdent: false,
  });

  const updateActiveFormats = useCallback((editor, selection) => {
    if (!editor || !selection || selection.rangeCount === 0) {
      return;
    }

    // Check formatting states using queryCommandState
    // Note: queryCommandState works even without focus in most browsers
    const formats = {
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      orderedList: document.queryCommandState("insertOrderedList"),
      unorderedList: document.queryCommandState("insertUnorderedList"),
      indent: false,
      outdent: false,
    };

    // Also check by traversing the DOM for more reliable detection
    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    let element =
      container.nodeType === Node.TEXT_NODE
        ? container.parentElement
        : container;

    // If element is not an Element node, try to get parent
    if (!element || element.nodeType !== Node.ELEMENT_NODE) {
      element = container.parentElement;
    }

    if (element) {
      // Check for bold (strong or b tag, or font-weight)
      if (!formats.bold) {
        const boldElement = element.closest("strong, b");
        if (boldElement) {
          formats.bold = true;
        } else {
          try {
            const style = window.getComputedStyle(element);
            const fontWeight = style.fontWeight;
            if (
              fontWeight === "bold" ||
              fontWeight === "bolder" ||
              parseInt(fontWeight) >= 700
            ) {
              formats.bold = true;
            }
          } catch (e) {
            // Ignore errors
          }
        }
      }

      // Check for italic (em or i tag, or font-style)
      if (!formats.italic) {
        const italicElement = element.closest("em, i");
        if (italicElement) {
          formats.italic = true;
        } else {
          try {
            const style = window.getComputedStyle(element);
            if (style.fontStyle === "italic" || style.fontStyle === "oblique") {
              formats.italic = true;
            }
          } catch (e) {
            // Ignore errors
          }
        }
      }

      // Check for underline (u tag or text-decoration)
      if (!formats.underline) {
        const underlineElement = element.closest("u");
        if (underlineElement) {
          formats.underline = true;
        } else {
          try {
            const style = window.getComputedStyle(element);
            if (
              style.textDecoration &&
              style.textDecoration.includes("underline")
            ) {
              formats.underline = true;
            }
          } catch (e) {
            // Ignore errors
          }
        }
      }

      // Check for lists
      const listItem = element.closest("li");
      if (listItem) {
        const parentList = listItem.parentElement;
        if (parentList) {
          if (parentList.tagName === "OL") {
            formats.orderedList = true;
            formats.unorderedList = false;
          } else if (parentList.tagName === "UL") {
            formats.unorderedList = true;
            formats.orderedList = false;
          }
        }

        // Check for indent/outdent availability
        // Indent: can indent if there's a previous sibling list item
        formats.indent = listItem.previousElementSibling !== null;

        // Outdent: can outdent if the list item is inside a nested list
        // (i.e., the parent list is inside another list item)
        const parentListItem = parentList?.parentElement;
        formats.outdent = parentListItem?.tagName === "LI";
      } else {
        // Not in a list item, so indent/outdent are not applicable
        formats.indent = false;
        formats.outdent = false;
      }

      // Check for color (font element or span with style color)
      let colorValue = null;
      let hasColor = false;

      // Get editor's default color for comparison
      let editorDefaultColor = null;
      try {
        const editorStyle = window.getComputedStyle(editor);
        editorDefaultColor = editorStyle.color;
      } catch (e) {
        // Ignore
      }

      // Check for font element with color attribute
      const fontElement = element.closest("font");
      if (fontElement && fontElement.hasAttribute("color")) {
        const colorAttr = fontElement.getAttribute("color");
        if (colorAttr && colorAttr.trim()) {
          hasColor = true;
          colorValue = colorAttr.trim();
        }
      }

      // Check for span or other elements with inline style color
      if (!hasColor) {
        let currentElement = element;
        // Walk up the DOM tree to find color
        while (currentElement && currentElement !== editor) {
          // Check inline style first (most reliable)
          if (currentElement.style && currentElement.style.color) {
            const inlineColor = currentElement.style.color.trim();
            if (inlineColor) {
              hasColor = true;
              colorValue = inlineColor;
              break;
            }
          }

          // For font and span elements, also check computed style
          if (
            currentElement.tagName === "FONT" ||
            currentElement.tagName === "SPAN"
          ) {
            try {
              const computedStyle = window.getComputedStyle(currentElement);
              const computedColor = computedStyle.color;

              // Check if color is different from editor's default (not inherited)
              if (computedColor && computedColor !== editorDefaultColor) {
                // Verify it's actually set on this element by checking inline style
                // or if it's a font/span element, it's likely intentional
                if (
                  currentElement.style.color ||
                  (currentElement.tagName === "FONT" &&
                    currentElement.hasAttribute("color")) ||
                  (currentElement.tagName === "SPAN" &&
                    currentElement.hasAttribute("style"))
                ) {
                  hasColor = true;
                  colorValue = computedColor;
                  break;
                }
              }
            } catch (e) {
              // Ignore errors
            }
          }

          currentElement = currentElement.parentElement;
        }
      }

      // Convert color value to hex format if needed
      if (hasColor && colorValue) {
        const hexColor = convertColorToHex(colorValue);
        if (hexColor) {
          formats.color = true;
          formats.colorValue = hexColor;
        } else {
          formats.color = false;
          formats.colorValue = null;
        }
      } else {
        formats.color = false;
        formats.colorValue = null;
      }
    }

    setActiveFormats(formats);

    // Update color picker input value if color is detected
    if (formats.color && formats.colorValue && colorInputRef.current) {
      colorInputRef.current.value = formats.colorValue;
    }
  }, []);

  // Helper function to convert color to hex format
  const convertColorToHex = (color) => {
    if (!color) return null;

    // If already hex format
    if (color.startsWith("#")) {
      return color.length === 4
        ? `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`
        : color;
    }

    // If rgb/rgba format
    if (color.startsWith("rgb")) {
      const matches = color.match(/\d+/g);
      if (matches && matches.length >= 3) {
        const r = parseInt(matches[0]).toString(16).padStart(2, "0");
        const g = parseInt(matches[1]).toString(16).padStart(2, "0");
        const b = parseInt(matches[2]).toString(16).padStart(2, "0");
        return `#${r}${g}${b}`.toUpperCase();
      }
    }

    // If named color, try to convert
    const tempDiv = document.createElement("div");
    tempDiv.style.color = color;
    document.body.appendChild(tempDiv);
    const computedColor = window.getComputedStyle(tempDiv).color;
    document.body.removeChild(tempDiv);

    if (computedColor.startsWith("rgb")) {
      const matches = computedColor.match(/\d+/g);
      if (matches && matches.length >= 3) {
        const r = parseInt(matches[0]).toString(16).padStart(2, "0");
        const g = parseInt(matches[1]).toString(16).padStart(2, "0");
        const b = parseInt(matches[2]).toString(16).padStart(2, "0");
        return `#${r}${g}${b}`.toUpperCase();
      }
    }

    return null;
  };

  useEffect(() => {
    const editor = getEditorElement(editorContentRef, editorContainerRef);
    if (!editor) return;

    const saveSelection = () => {
      const selection = window.getSelection();

      if (
        selection &&
        selection.rangeCount > 0 &&
        editor.contains(selection.anchorNode)
      ) {
        storeSelection(selection.getRangeAt(0), lastSelectionRef);
        updateActiveFormats(editor, selection);
      }
    };

    editor.addEventListener("keyup", saveSelection);
    editor.addEventListener("keydown", saveSelection);
    editor.addEventListener("mouseup", saveSelection);
    editor.addEventListener("input", saveSelection);
    editor.addEventListener("click", saveSelection);

    return () => {
      editor.removeEventListener("keyup", saveSelection);
      editor.removeEventListener("keydown", saveSelection);
      editor.removeEventListener("mouseup", saveSelection);
      editor.removeEventListener("input", saveSelection);
      editor.removeEventListener("click", saveSelection);
    };
  }, [editorContentRef, editorContainerRef, updateActiveFormats]);

  useEffect(() => {
    const handleSelectionChange = () => {
      const editor = getEditorElement(editorContentRef, editorContainerRef);
      if (!editor) return;
      const selection = window.getSelection();
      if (
        selection &&
        selection.rangeCount > 0 &&
        editor.contains(selection.anchorNode)
      ) {
        storeSelection(selection.getRangeAt(0), lastSelectionRef);
        updateActiveFormats(editor, selection);
      } else {
        // Reset formats when selection is outside editor
        setActiveFormats({
          bold: false,
          italic: false,
          underline: false,
          orderedList: false,
          unorderedList: false,
          color: false,
          colorValue: null,
          indent: false,
          outdent: false,
        });
      }
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, [editorContentRef, editorContainerRef, updateActiveFormats]);

  const handleClearFormatting = () => {
    const editor = getEditorElement(editorContentRef);
    if (editor) {
      editor.focus();
      clearAllFormatting();
      const event = new Event("input", { bubbles: true });
      editor.dispatchEvent(event);
    }
  };

  const executeCommand = (command, value = "") => {
    const editor = getEditorElement(editorContentRef, editorContainerRef);
    if (!editor) {
      return;
    }
    editor.focus();

    // First, try to use the current selection if it's valid and within the editor
    let selection = window.getSelection();
    let range = null;

    if (selection && selection.rangeCount > 0) {
      const currentRange = selection.getRangeAt(0);
      // Check if the current selection is within the editor
      if (editor.contains(currentRange.commonAncestorContainer)) {
        range = currentRange;
      }
    }

    // If no valid current selection, try to restore from stored selection
    if (!range) {
      selection = restoreSelection(editor, lastSelectionRef);
      if (!selection || selection.rangeCount === 0) {
        return;
      }
      range = selection.getRangeAt(0);

      // Verify the restored range is within the editor
      if (!editor.contains(range.commonAncestorContainer)) {
        // If restored range is invalid, try to get current selection again
        selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const currentRange = selection.getRangeAt(0);
          if (editor.contains(currentRange.commonAncestorContainer)) {
            range = currentRange;
          } else {
            return;
          }
        } else {
          return;
        }
      }
    }

    // Update the selection to ensure it's set correctly
    if (selection && range) {
      selection.removeAllRanges();
      selection.addRange(range);
    }

    // Handle list commands (insertOrderedList, insertUnorderedList)
    if (command === "insertOrderedList" || command === "insertUnorderedList") {
      const container = range.commonAncestorContainer;
      let element =
        container.nodeType === Node.TEXT_NODE
          ? container.parentElement
          : container;

      // If we're already in a list, just toggle it
      const listItem = element?.closest("li");
      if (listItem) {
        document.execCommand(command, false, value);
        const event = new Event("input", { bubbles: true });
        editor.dispatchEvent(event);
        const updatedSelection = window.getSelection();
        if (updatedSelection && updatedSelection.rangeCount > 0) {
          storeSelection(updatedSelection.getRangeAt(0), lastSelectionRef);
          setTimeout(() => {
            updateActiveFormats(editor, updatedSelection);
          }, 0);
        }
        return;
      }
      // Execute the list command
      document.execCommand(command, false, value);
    } else if (command === "indent" || command === "outdent") {
      // Get the common ancestor container, which might be a Text node
      const container = range.commonAncestorContainer;
      // If it's not an Element, get its parent element
      const element =
        container.nodeType === Node.TEXT_NODE
          ? container.parentElement
          : container;
      const listItem = element?.closest("li");
      if (listItem) {
        if (command === "indent") {
          // Create nested list by moving item into previous item's nested list
          const parentList = listItem.parentElement;
          const prevItem = listItem.previousElementSibling;
          if (prevItem) {
            let nestedList = prevItem.querySelector("ol, ul");
            if (!nestedList) {
              nestedList = document.createElement(parentList.tagName);
              prevItem.appendChild(nestedList);
            }
            nestedList.appendChild(listItem);
            const event = new Event("input", { bubbles: true });
            editor.dispatchEvent(event);

            // Update active formats after indent
            setTimeout(() => {
              const updatedSelection = window.getSelection();
              if (updatedSelection && updatedSelection.rangeCount > 0) {
                storeSelection(
                  updatedSelection.getRangeAt(0),
                  lastSelectionRef
                );
                updateActiveFormats(editor, updatedSelection);
              }
            }, 0);
            return;
          }
        } else if (command === "outdent") {
          // Move item out of nested list
          const parentList = listItem.parentElement;
          if (
            parentList &&
            (parentList.tagName === "OL" || parentList.tagName === "UL")
          ) {
            const grandParentList = parentList.parentElement;
            if (
              grandParentList &&
              (grandParentList.tagName === "OL" ||
                grandParentList.tagName === "UL")
            ) {
              // Move to grandparent list
              grandParentList.insertBefore(listItem, parentList.nextSibling);
              if (parentList.children.length === 0) {
                parentList.remove();
              }
              const event = new Event("input", { bubbles: true });
              editor.dispatchEvent(event);

              // Update active formats after outdent
              setTimeout(() => {
                const updatedSelection = window.getSelection();
                if (updatedSelection && updatedSelection.rangeCount > 0) {
                  storeSelection(
                    updatedSelection.getRangeAt(0),
                    lastSelectionRef
                  );
                  updateActiveFormats(editor, updatedSelection);
                }
              }, 0);
              return;
            } else {
              // Move to parent level (out of list entirely)
              const parentListItem = parentList.parentElement;
              if (parentListItem && parentListItem.tagName === "LI") {
                parentListItem.parentElement.insertBefore(
                  listItem,
                  parentListItem.nextSibling
                );
                if (parentList.children.length === 0) {
                  parentList.remove();
                }
                const event = new Event("input", { bubbles: true });
                editor.dispatchEvent(event);

                // Update active formats after outdent
                setTimeout(() => {
                  const updatedSelection = window.getSelection();
                  if (updatedSelection && updatedSelection.rangeCount > 0) {
                    storeSelection(
                      updatedSelection.getRangeAt(0),
                      lastSelectionRef
                    );
                    updateActiveFormats(editor, updatedSelection);
                  }
                }, 0);
                return;
              }
            }
          }
        }
      }
      // Fallback to execCommand if manual handling didn't work
      document.execCommand(command, false, value);
    } else {
      // Execute the command for other commands (including insertOrderedList)
      document.execCommand(command, false, value);
    }

    // Trigger onChange manually
    const event = new Event("input", { bubbles: true });
    editor.dispatchEvent(event);

    const updatedSelection = window.getSelection();
    if (updatedSelection && updatedSelection.rangeCount > 0) {
      storeSelection(updatedSelection.getRangeAt(0), lastSelectionRef);
      // Update active formats after command execution
      setTimeout(() => {
        updateActiveFormats(editor, updatedSelection);
      }, 0);
    }
  };

  const handleColorChange = (e) => {
    const editor = getEditorElement(editorContentRef, editorContainerRef);
    if (editor) {
      editor.focus();
      document.execCommand("styleWithCSS", true, null);
      document.execCommand("foreColor", false, e.target.value);
      const event = new Event("input", { bubbles: true });
      editor.dispatchEvent(event);

      // Update active formats after color change
      setTimeout(() => {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          updateActiveFormats(editor, selection);
        }
      }, 0);
    }
  };
  function insertColoredText(text, editor, providedRange = null) {
    // Get or create selection
    let selection = window.getSelection();
    let range;

    // Use provided range if available (more reliable)
    if (providedRange) {
      range = providedRange;

      // Set the selection to the provided range
      if (selection) {
        selection.removeAllRanges();
        // First, try to use the stored range directly if it's still valid
        try {
          // Check if the stored range's containers are still in the DOM and within the editor
          const startContainer = providedRange.startContainer;
          const endContainer = providedRange.endContainer;

          // Verify containers are still valid and within editor
          if (
            editor.contains(startContainer) &&
            editor.contains(endContainer)
          ) {
            // Try to restore the exact range
            const newRange = document.createRange();
            newRange.setStart(startContainer, providedRange.startOffset);
            newRange.setEnd(endContainer, providedRange.endOffset);

            // Verify the range is valid
            if (
              newRange.startContainer === startContainer &&
              newRange.endContainer === endContainer
            ) {
              selection.addRange(newRange);
              // return selection;
            } else {
              selection.addRange(range);
            }
          } else {
            selection.addRange(range);
          }
        } catch (e) {
          selection.addRange(range);
          // Range is invalid, continue to fallback logic
        }
        range = selection.getRangeAt(0);
      }
    } else {
      if (!selection || !selection.rangeCount) {
        // If no selection, create one at the end of editor
        range = document.createRange();
        range.selectNodeContents(editor);
        range.collapse(false);
        selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
      } else {
        range = selection.getRangeAt(0);
      }
    }

    if (
      range.startContainer === editor ||
      (range.startContainer.nodeType !== Node.TEXT_NODE &&
        range.startContainer.parentElement === editor)
    ) {
      if (range.startContainer === editor) {
        const children = Array.from(editor.childNodes);
        const offset = range.startOffset;

        // Find the paragraph or element that should contain our insertion
        let targetElement = null;

        if (offset > 0 && offset <= children.length) {
          // Insert at end of the element before offset
          targetElement = children[offset - 1];
          if (targetElement.nodeType === Node.ELEMENT_NODE) {
            // Find last text node in this element
            const walker = document.createTreeWalker(
              targetElement,
              NodeFilter.SHOW_TEXT,
              null
            );
            const textNodes = [];
            let node;
            while ((node = walker.nextNode())) {
              textNodes.push(node);
            }
            if (textNodes.length > 0) {
              const lastTextNode = textNodes[textNodes.length - 1];
              range.setStart(lastTextNode, lastTextNode.textContent.length);
              range.collapse(true);
            } else {
              // No text nodes, append to element
              range.setStart(targetElement, targetElement.childNodes.length);
              range.collapse(true);
            }
          }
        } else if (offset < children.length) {
          // Insert at start of element at offset
          targetElement = children[offset];
          if (targetElement.nodeType === Node.ELEMENT_NODE) {
            const walker = document.createTreeWalker(
              targetElement,
              NodeFilter.SHOW_TEXT,
              null
            );
            const firstTextNode = walker.nextNode();
            if (firstTextNode) {
              range.setStart(firstTextNode, 0);
              range.collapse(true);
            } else {
              range.setStart(targetElement, 0);
              range.collapse(true);
            }
          }
        }
      }

      // If still not in a text node, find the last text node in editor
      if (
        range.startContainer.nodeType !== Node.TEXT_NODE &&
        range.startContainer !== editor
      ) {
        const walker = document.createTreeWalker(
          range.startContainer || editor,
          NodeFilter.SHOW_TEXT,
          null
        );
        const textNodes = [];
        let node;
        while ((node = walker.nextNode())) {
          textNodes.push(node);
        }
        if (textNodes.length > 0) {
          const lastTextNode = textNodes[textNodes.length - 1];
          range.setStart(lastTextNode, lastTextNode.textContent.length);
          range.collapse(true);
        }
      }
    }

    // Delete any selected content
    range.deleteContents();

    // Create the font element with the placeholder text
    const font = document.createElement("font");
    font.setAttribute("color", "#587e85");
    font.innerHTML = `<b>${text}</b>`;

    // Insert the font element
    range.insertNode(font);

    // Immediately set cursor position after the font element
    const newRange = document.createRange();
    try {
      newRange.setStartAfter(font);
      newRange.collapse(true);

      if (newRange.startContainer === editor) {
        // Find the first text node after the font element
        let nextTextNode = null;

        if (font.nextSibling) {
          if (font.nextSibling.nodeType === Node.TEXT_NODE) {
            nextTextNode = font.nextSibling;
          } else {
            const walker = document.createTreeWalker(
              font.nextSibling,
              NodeFilter.SHOW_TEXT,
              null
            );
            nextTextNode = walker.nextNode();
          }
        }

        // If no next sibling, check parent's next sibling
        if (!nextTextNode && font.parentNode && font.parentNode !== editor) {
          const fontParent = font.parentNode;
          if (fontParent.nextSibling) {
            if (fontParent.nextSibling.nodeType === Node.TEXT_NODE) {
              nextTextNode = fontParent.nextSibling;
            } else {
              const walker = document.createTreeWalker(
                fontParent.nextSibling,
                NodeFilter.SHOW_TEXT,
                null
              );
              nextTextNode = walker.nextNode();
            }
          }
        }

        if (nextTextNode) {
          newRange.setStart(nextTextNode, 0);
          newRange.collapse(true);
        } else {
          // No next text node found, place cursor at end of editor
          newRange.selectNodeContents(editor);
          newRange.collapse(false);
        }
      }
    } catch (e) {
      newRange.selectNodeContents(editor);
      newRange.collapse(false);
    }

    // Update selection immediately
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(newRange);
    }

    // Store the new selection
    storeSelection(newRange, lastSelectionRef);

    // Return the font element so we can restore cursor after input event
    return font;
  }

  const handlePlaceholderClick = (placeholderText) => {
    const editor = getEditorElement(editorContentRef, editorContainerRef);
    if (!editor) {
      return;
    }

    // Close the dropdown first to prevent interference
    setPlaceholderDropdownOpen((prev) => ({
      ...prev,
      [editorKey]: false,
    }));

    // Focus the editor
    editor.focus();

    // Restore the previously saved selection (before dropdown was clicked)
    const restoredSelection = restoreSelection(editor, lastSelectionRef);

    // Get the range immediately after restoration (before it gets lost)
    let rangeToUse = null;
    if (restoredSelection && restoredSelection.rangeCount > 0) {
      rangeToUse = restoredSelection.getRangeAt(0).cloneRange();
    }

    // Set a flag on the editor to prevent ref callback from interfering
    editor.setAttribute("data-updating", "true");
    const insertedValue =
      contractData[
        replaceableValue.find((item) => item.text === placeholderText).key
      ];

    // Initialize placeholderText to use for insertion
    let finalPlaceholderText = placeholderText;

    if (insertedValue) {
      const valueInObject = insertedValue;
      if (valueInObject) {
        let markwillbeplaceHolder;
        if (insertedValue instanceof Date) {
          markwillbeplaceHolder = moment(insertedValue).format("DD-MM-YYYY");
        } else {
          markwillbeplaceHolder = insertedValue?.trim();
        }

        if (
          markwillbeplaceHolder &&
          markwillbeplaceHolder.length > placeholderText.length
        ) {
          // Calculate the number of spaces needed
          const spaceCount =
            markwillbeplaceHolder.length - placeholderText.length;
          // Add spaces to the placeholder text
          finalPlaceholderText = placeholderText + " ".repeat(spaceCount);
        }
      }
    }
    // Insert the placeholder text at the restored cursor position
    // Pass the cloned range directly to avoid relying on window.getSelection()
    const fontElement = insertColoredText(
      finalPlaceholderText,
      editor,
      rangeToUse
    );

    // Clear the flag after a short delay
    setTimeout(() => {
      editor.removeAttribute("data-updating");
    }, 200);

    // Trigger onChange
    const event = new Event("input", { bubbles: true });
    editor.dispatchEvent(event);

    // Calculate text offset after the inserted placeholder
    const calculateTextOffsetAfterFont = (editor, font) => {
      const walker = document.createTreeWalker(
        editor,
        NodeFilter.SHOW_TEXT,
        null
      );
      let offset = 0;
      let node;
      while ((node = walker.nextNode())) {
        if (font.contains(node)) {
          // Add the length of text inside font
          offset += node.textContent.length;
          // Continue to find text after font
        } else if (
          font.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING
        ) {
          // This text node comes after font, we've found our position
          break;
        } else {
          offset += node.textContent.length;
        }
      }
      return offset;
    };

    const textOffsetAfterPlaceholder = fontElement
      ? calculateTextOffsetAfterFont(editor, fontElement)
      : null;

    requestAnimationFrame(() => {
      const sel = window.getSelection();

      if (sel && textOffsetAfterPlaceholder !== null) {
        try {
          // Restore cursor using text offset instead of font element reference
          const walker = document.createTreeWalker(
            editor,
            NodeFilter.SHOW_TEXT,
            null
          );

          let currentOffset = 0;
          let targetNode = null;
          let targetOffset = 0;

          let node;
          while ((node = walker.nextNode())) {
            const nodeLength = node.textContent.length;
            if (currentOffset + nodeLength >= textOffsetAfterPlaceholder) {
              targetNode = node;
              targetOffset = textOffsetAfterPlaceholder - currentOffset;
              break;
            }
            currentOffset += nodeLength;
          }

          if (targetNode) {
            const restoredRange = document.createRange();
            restoredRange.setStart(
              targetNode,
              Math.min(targetOffset, targetNode.textContent.length)
            );
            restoredRange.collapse(true);

            sel.removeAllRanges();
            sel.addRange(restoredRange);
            storeSelection(restoredRange, lastSelectionRef);
          } else {
            // Fallback: place at end
            const restoredRange = document.createRange();
            restoredRange.selectNodeContents(editor);
            restoredRange.collapse(false);
            sel.removeAllRanges();
            sel.addRange(restoredRange);
            storeSelection(restoredRange, lastSelectionRef);
          }

          // Ensure editor has focus
          if (document.activeElement !== editor) {
            editor.focus();
          }
        } catch (e) {
          // If cursor restoration fails, at least ensure editor has focus
          editor.focus();
        }
      }
    });
  };

  return (
    <div className="my-custom-class custom-editor-toolbar">
      {/* Formatting Buttons */}
      <button
        type="button"
        className={`pell-button ${activeFormats.bold ? "actives" : ""}`}
        onClick={() => executeCommand("bold")}
        title="Bold"
      >
        <img src={activeFormats.bold ? BoldActive : Bold} alt="bold" />
      </button>
      <button
        type="button"
        className={`pell-button ${activeFormats.italic ? "actives" : ""}`}
        onClick={() => executeCommand("italic")}
        title="Italic"
      >
        {/* <em>I</em> */}
        <img src={activeFormats.italic ? ItalicActive : Italic} alt="bold" />
      </button>
      <button
        type="button"
        className={`pell-button ${activeFormats.underline ? "actives" : ""}`}
        onClick={() => executeCommand("underline")}
        title="Underline"
      >
        {/* <u>U</u> */}
        <img
          src={activeFormats.underline ? UnderlineActive : Underline}
          alt="bold"
        />
      </button>

      {/* List Buttons */}
      <button
        type="button"
        className={`pell-button ${activeFormats.orderedList ? "actives" : ""}`}
        onClick={() => executeCommand("insertOrderedList")}
        title="Ordered List"
      >
        <img
          src={activeFormats.orderedList ? NumberListActive : NumberList}
          alt="bold"
        />
      </button>
      <button
        type="button"
        className={`pell-button ${
          activeFormats.unorderedList ? "actives" : ""
        }`}
        onClick={() => executeCommand("insertUnorderedList")}
        title="Unordered List"
      >
        <img
          src={activeFormats.unorderedList ? BulletActive : Bullet}
          alt="bold"
        />
      </button>
      <button
        type="button"
        className={`pell-button ${activeFormats.indent ? "actives" : ""}`}
        onClick={() => executeCommand("indent")}
        title="Indent"
      >
        <img src={activeFormats.indent ? IndentActive : Indent} alt="bold" />
      </button>
      <button
        type="button"
        className={`pell-button ${activeFormats.outdent ? "actives" : ""}`}
        onClick={() => executeCommand("outdent")}
        title="Outdent"
      >
        <img src={activeFormats.indent ? OutdentActive : Outdent} alt="bold" />
      </button>

      {/* Color Picker */}
      <button
        type="button"
        className={`pell-button toolbar-color-picker ${
          activeFormats.color ? "actives" : ""
        }`}
        title="Color Picker"
      >
        <img
          src={activeFormats.color ? PickerActive : Picker}
          alt="picker"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (colorInputRef.current) {
              colorInputRef.current.click();
            }
          }}
        />
        <input
          ref={colorInputRef}
          type="color"
          className="color-picker-input"
          onInput={handleColorChange}
          defaultValue="#000000"
        />
      </button>
      {/* Clear Button */}
      <button
        type="button"
        className="pell-button"
        onClick={() => handleClearFormatting()}
        title="Clear"
      >
        <img src={Clear} alt="bold" />
      </button>
      {/* Placeholder Dropdown Button */}
      <div className="placeholder-wrapper d-flex align-items-center">
        <button
          type="button"
          className="pell-button placeholder-button"
          id={`placeholder-button-${editorKey}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            // Store the current selection before opening dropdown
            const editor = getEditorElement(
              editorContentRef,
              editorContainerRef
            );
            if (editor) {
              const selection = window.getSelection();
              if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                // Only store if the selection is within the editor
                if (editor.contains(range.commonAncestorContainer)) {
                  storeSelection(range, lastSelectionRef);
                }
              }
            }

            setPlaceholderDropdownOpen((prev) => ({
              ...prev,
              [editorKey]: !prev[editorKey],
            }));
          }}
          title="Insert Placeholder"
        >
          {placeholderDropdownOpen[editorKey] ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            >
              <rect width="18" height="18" rx="2" fill="#DFE7C1" />
              <path
                d="M2.63008 12.0562C2.43883 12.0562 2.27852 11.9915 2.14914 11.8621C2.01977 11.7328 1.95508 11.5725 1.95508 11.3812V5.5312C1.95508 5.33995 2.01977 5.17964 2.14914 5.05026C2.27852 4.92089 2.43883 4.8562 2.63008 4.8562H13.8801C14.0713 4.8562 14.2316 4.92089 14.361 5.05026C14.4904 5.17964 14.5551 5.33995 14.5551 5.5312V6.4312C14.5551 6.62245 14.4908 6.78276 14.3621 6.91214C14.2335 7.04151 14.0741 7.1062 13.884 7.1062C13.6939 7.1062 13.5332 7.04151 13.402 6.91214C13.2707 6.78276 13.2051 6.62245 13.2051 6.4312V6.2062H3.30508V10.7062H9.38008C9.57133 10.7062 9.73164 10.7705 9.86102 10.8991C9.99039 11.0278 10.0551 11.1871 10.0551 11.3773C10.0551 11.5674 9.99039 11.7281 9.86102 11.8593C9.73164 11.9906 9.57133 12.0562 9.38008 12.0562H2.63008ZM12.7551 10.7625V12.2812C12.7551 12.4725 12.6908 12.6328 12.5621 12.7621C12.4335 12.8915 12.2741 12.9562 12.084 12.9562C11.8939 12.9562 11.7332 12.8915 11.602 12.7621C11.4707 12.6328 11.4051 12.4725 11.4051 12.2812V9.1312C11.4051 8.93995 11.4698 8.77964 11.5991 8.65026C11.7285 8.52089 11.8888 8.4562 12.0801 8.4562H15.2301C15.4213 8.4562 15.5816 8.52051 15.711 8.64914C15.8404 8.77776 15.9051 8.93714 15.9051 9.12726C15.9051 9.31739 15.8404 9.47808 15.711 9.60933C15.5816 9.74058 15.4213 9.8062 15.2301 9.8062H13.7113L15.8863 11.9812C16.0238 12.1146 16.0926 12.2729 16.0926 12.4561C16.0926 12.6395 16.0238 12.8 15.8863 12.9375C15.7488 13.075 15.5884 13.1437 15.405 13.1437C15.2218 13.1437 15.0635 13.075 14.9301 12.9375L12.7551 10.7625Z"
                fill="#2D3245"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            >
              <path
                d="M2.63008 12.0562C2.43883 12.0562 2.27852 11.9915 2.14914 11.8621C2.01977 11.7328 1.95508 11.5725 1.95508 11.3812V5.5312C1.95508 5.33995 2.01977 5.17964 2.14914 5.05026C2.27852 4.92089 2.43883 4.8562 2.63008 4.8562H13.8801C14.0713 4.8562 14.2316 4.92089 14.361 5.05026C14.4904 5.17964 14.5551 5.33995 14.5551 5.5312V6.4312C14.5551 6.62245 14.4908 6.78276 14.3621 6.91214C14.2335 7.04151 14.0741 7.1062 13.884 7.1062C13.6939 7.1062 13.5332 7.04151 13.402 6.91214C13.2707 6.78276 13.2051 6.62245 13.2051 6.4312V6.2062H3.30508V10.7062H9.38008C9.57133 10.7062 9.73164 10.7705 9.86102 10.8991C9.99039 11.0278 10.0551 11.1871 10.0551 11.3773C10.0551 11.5674 9.99039 11.7281 9.86102 11.8593C9.73164 11.9906 9.57133 12.0562 9.38008 12.0562H2.63008ZM12.7551 10.7625V12.2812C12.7551 12.4725 12.6908 12.6328 12.5621 12.7621C12.4335 12.8915 12.2741 12.9562 12.084 12.9562C11.8939 12.9562 11.7332 12.8915 11.602 12.7621C11.4707 12.6328 11.4051 12.4725 11.4051 12.2812V9.1312C11.4051 8.93995 11.4698 8.77964 11.5991 8.65026C11.7285 8.52089 11.8888 8.4562 12.0801 8.4562H15.2301C15.4213 8.4562 15.5816 8.52051 15.711 8.64914C15.8404 8.77776 15.9051 8.93714 15.9051 9.12726C15.9051 9.31739 15.8404 9.47808 15.711 9.60933C15.5816 9.74058 15.4213 9.8062 15.2301 9.8062H13.7113L15.8863 11.9812C16.0238 12.1146 16.0926 12.2729 16.0926 12.4561C16.0926 12.6395 16.0238 12.8 15.8863 12.9375C15.7488 13.075 15.5884 13.1437 15.405 13.1437C15.2218 13.1437 15.0635 13.075 14.9301 12.9375L12.7551 10.7625Z"
                fill="#848B92"
              />
            </svg>
          )}
        </button>
        {placeholderDropdownOpen[editorKey] && (
          <div
            id={`placeholder-dropdown-${editorKey}`}
            className="placeholder-dropdown"
          >
            <div className="placeholder-dropdown__header">Select Option</div>
            {replaceableValue.map((item, idx) => (
              <div
                key={idx}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handlePlaceholderClick(item.text);
                }}
                className="placeholder-option"
              >
                {item.text}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomEditorToolbar;
